import { computed, reactive, ref, Ref, toRaw, watch } from 'vue'
import { defineStore } from 'pinia'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { v4 } from 'uuid'
import qs from 'qs'
import SharedWorker, {
  SharedWorkerMode
} from '@voghion/shared-worker'
import { useSetting } from '@/composables/use-setting'
import {
  fetchMessageList,
  fetchChatList,
  fetchEndpointConfig,
  clearAllChat,
  fetchEvaluationConfig,
  fetchDiversionIM,
} from '@/api/msg.js'
import { handleUploadFile } from '@/api/request'
import { generateCircleWithLetter, stringToBase64 } from '@/utils/tool.js'
import {
  filterMsgTypeInfo,
  filterMsgSendInfo,
  isSingleRoom,
  filterRoom,
  reviewUser,
  getResource,
  getIsPlatformPC
} from '@/utils/msgFilter'
import { trackGAEvent } from '@/utils/sensors'
import { jsBridge, EVENT_VENDOR_CALLBACK } from '@voghion/native-api'

// @ts-ignore
import workerScript from '@/worker/index.js?worker&url'
import { Singleflight, dispatchEvent, getLocalStorage } from '@voghion/fe-util'
import { generateUUID } from '@/utils/helper'
import {
  IMMessage,
  ContentMessageToSend,
  Room,
  IMMessageType,
  MessageListUpdateMode,
  EVENT_SEND_MESSAGE_FAILURE
} from '@/utils/constant'
import {
  GetChatListRequest
} from '@/worker/protocol'
import { getLastMessageContent, isMessagePrepared } from '@/utils/message-helper'

import { wsLangSetting } from '@/composables/use-ws-lang'
import { formatLocalTime } from '@/utils/time'
import { useEnvStore } from './env'
import { throttle, debounce } from 'throttle-debounce'
import { cacheRooms, getCacheRooms } from '@/utils/cache'

const wsSetting = wsLangSetting()

const singleflight = Singleflight.getInstance()

//@ts-ignore
// const workerScript = new URL('../worker.js', import.meta.url);
const worker = new SharedWorker(workerScript, {
  name: 'im',
  // debug: true,
  mode: process.env.NODE_ENV === 'production' ? SharedWorkerMode.Auto : SharedWorkerMode.Simulate,
  type: 'module'
})

window['debugWorker'] = worker

const hashStr = window.location.hash.slice(3)
const parseData = qs.parse(hashStr)

const { toUserType, toUserRole, toCombineId } = parseData

const defaultToUserType = toUserType || 'GROUP'
const defaultToUserRole = toUserRole || 'GROUP_KEY_ACCOUNT_MANAGER_SALES_BEFORE'
const defaultToCombinId = toCombineId || 1

// const headColorList = ["#FFBF58", "#6D87E6", "#B077E9", "#5AD28A", "#FF865F"];

// const reviewUser = { _id: 'clickReview', username: 'Click here' }

// const wsHost = getEnvImURL();

interface imUserDataType {
  imUserIdStr?: string
  headImage?: string
  imUserType?: number | string
  nickname?: string
  imUserId?: number
}

type welcomeMsgType = {
  content?: string
  contentTranslate?: string
  messageId?: number
  needAck?: boolean
  nickname?: string
  right?: boolean
  sessionId?: string
}
type createRoomType = {
  toCombineId?: string | number
  toUserType?: string
  toUserRole?: string
  pageFrom?: string
  extParam?: string
}

type staticTranslateType = {
  orderTimeTxt?: string
  orderIdTxt?: string
  sendTxt?: string
}

interface SyncStoreData extends Record<string, any> {
  action: string
}

const messagesPromiseModule = {}
const currentPageId = generateUUID()
// /**
//  * 调用 worker api
//  * @param type 类型
//  * @param data 数据
//  * @param callbackId 回调id
//  * @param timeout 超时时间
//  * @returns
//  */
// const callWorkerApi = <T>(
//   type: string,
//   data: any,
//   callbackId: string,
//   timeout: number = 0
// ) => {
//   return new Promise<T>((resolve) => {
//     worker.postMessage({ type, data, pageId: currentPageId });
//     messagesPromiseModule[callbackId] = { resolve };
//   }).finally(() => {
//     messagesPromiseModule[callbackId] = null;
//     delete messagesPromiseModule[callbackId];
//   });
// };
/**
 * 触发 worker api 回调
 * @param callbackId
 * @param data
 */
const handleWorkerApiCallback = (callbackId: string, response: any) => {
  if (messagesPromiseModule[callbackId]) {
    messagesPromiseModule[callbackId].resolve(response)
  }
}

const SEND_MESSAGE_TIMEOUT = 5000 // 发送消息超时时间

/**
 * 消息
 * 接口调用顺序
 * 1. 建立 socket 连接
 * 2. 收到 im 用户信息
 * 3. 获取会话列表
 * 4. 如果是单房间，创建房间，然后获取消息列表
 */
export const useMsgStore = defineStore('msg', () => {
  const route = useRoute()
  const { t } = useI18n()

  const entryInfo = {
    ...route.query
  }

  const wsStatus: Ref<'close' | 'open'> = ref('close')
  /**
   * socket 是否已就绪
   */
  const socketReady = ref<boolean>(false)
  /**
   * socket 首次连接
   */
  const socketFirstConnect = ref<boolean>(true)
  const roomsListOpened = ref(true)
  const roomChatOpen = ref(false)
  const imUserData: Ref<imUserDataType> = ref({})
  const rooms = ref(getCacheRooms() || [])
  const loadingRooms = ref(true)
  const sdkLoadingRooms = ref(rooms.value.length === 0)
  const chatsLoaded = ref(rooms.value.length > 0)
  const messagesLoaded = ref(false)
  const messagesLoading = ref(false)
  const roomsTotal = ref(0)
  const messagesAll = ref({})
  const selectedConversationId: Ref<number | null> = ref(null)
  const welcomeMsg: Ref<welcomeMsgType> = ref({})
  const defaultCreateRoomParams: Ref<createRoomType> = ref({})
  const staticInfo: Ref<staticTranslateType> = ref({
    orderIdTxt: '',
    orderTimeTxt: '',
    sendTxt: ''
  })
  const wsIsError = ref(false)
  const wsLoading = ref(false)
  const { getClientInfo } = useSetting()
  const pageSize = 20
  const chatPageSize = 100

  const catchMessage = reactive({
    content: '',
    replyMessage: '',
    files: []
  })
  const createRoomCatch = ref({
    applyId: '',
    catchConversationId: 0,
  })

  /**
   * 会话列表是否需要刷新
   */
  const chatListNeedReload = ref(true)

  const warnMsgId = ref(0)
  const waitMsgId = ref(0)
  const tabMsgId = ref(0)

  const messages = computed(() => {
    return messagesAll.value[selectedConversationId.value || ''] || []
  })

  const clearCatchMessage = () => {
    catchMessage.content = ''
    catchMessage.replyMessage = ''
    catchMessage.files = []
  }

  // 根据converstaionId获取roomInfo
  const getRoomInfoByConversationId = (conversationId) => {
    const selectedIndex = rooms.value?.findIndex((room) => room.roomId === conversationId)
    return selectedIndex > -1 ? rooms.value[selectedIndex] : {}
  }

  const latestCreateRoomMessage = ref<{
    type: string
    data: string
  } | null>(null)

  const envStore = useEnvStore()
  watch(
    () => {
      return envStore.windowVisible
    },
    (visible) => {
      if (visible) {
        if (latestCreateRoomMessage.value) {
          worker.postMessage(toRaw(latestCreateRoomMessage.value))
          latestCreateRoomMessage.value = null
        }
      }
    }
  )

  const doCreateRoom = (e: {
    conversationId?: number
    categoryExperts?: number | null
    toUserRole?: any
    toUserType?: string
  }) => {
    const resource = getResource()
    const clientInfo = getClientInfo()

    const applyId = v4().replace(/-/g, '').toUpperCase().slice(0, 20)

    let source = 'UNKNOWN'
    if (clientInfo.appSystem === 'pc') {
      source = 'PC'
    } else if (clientInfo.appSystem === 'h5') {
      source = 'H5'
    } else {
      source = 'APP'
    }

    const createRoomData = {
      resourceName: resource.pageFrom || 'imConversationList', // 'goodsDetail', //  'homeBottomButton'
      applyId,
      toUserType: e.toUserType || defaultToUserType,
      toUserRole: e.toUserRole || defaultToUserRole,
      country: clientInfo.country,
      currencyCode: clientInfo.currency,
      deviceCode: clientInfo.deviceCode,
      language: (wsSetting.wsLocaleLang.value as any)?.short || clientInfo.language,
      conversationId: e.conversationId || null,
      source,
      categoryExperts: e?.categoryExperts || -1 > -1 ? e.categoryExperts : resource.categoryExperts || 0
    }
    // 用于处理初次创建自定义的房间，需要缓存起来负数的值
    if (e?.conversationId && e?.conversationId < -1) {
      createRoomCatch.value = {
        applyId,
        catchConversationId: e?.conversationId,
      }
    }

    const promise = new Promise((resolve) => {
      if (wsStatus.value !== 'open') {
        return setTimeout(() => {
          resolve(true)
        })
      }
      /**
       * 窗口可见时再发送报文
       */
      if (!envStore.windowVisible) {
        latestCreateRoomMessage.value = {
          type: 'msg',
          data: `20023:${JSON.stringify(createRoomData)}`
        }
      } else {
        latestCreateRoomMessage.value = null
        worker.postMessage({
          type: 'msg',
          data: `20023:${JSON.stringify(createRoomData)}`
        })
      }
      messagesPromiseModule[`createroom_${applyId}`] = { resolve }
    }).finally(() => {
      messagesPromiseModule[`createroom_${applyId}`] = null
      delete messagesPromiseModule[`createroom_${applyId}`]
    })
    return promise
  }

  const doDisconnect = () => {
    // 先标识已断开，避免快速切换卡在 loading 的问题
    wsStatus.value = 'close'
    worker.postMessage({ type: 'disconnect' })
  }

  /**
   * worker 和当前页面的关联是否初始化
   */
  const workerInited = ref(false)

  /**
   * 自动重连标识，用于 worker 异常时
   */
  const autoConnect = ref(false)

  // 监听worker更新的message事件，不是方法！
  worker.onmessage = async (event) => {
    const info = event.data || {}
    const { type, data, wsStatusInfo, pageId } = info
    switch (type) {
      case 'ready':
        workerInited.value = false
        autoConnect.value = true
        initWorker(false)
        break
      case 'init':
        if (workerInited.value || pageId !== currentPageId) {
          return
        }
        workerInited.value = true
        wsStatus.value = data.wsStatus
        wsLoading.value = data.readyState !== 1
        if (data.wsStatus === 'open' && data.wsUserBaseInfo) {
          handleData.receiveIMUserInfo(data.wsUserBaseInfo)
        } else {
          if (autoConnect.value || data.autoConnect) {
            autoConnect.value = false
            doReconnect()
          }
        }
        break
      case 'receive':
        handleReceiveMessage(data)
        break
      case 'statusChange':
        const statusInfo = JSON.parse(wsStatusInfo)
        const { wsStatus: status, readyState } = statusInfo
        wsStatus.value = status || 'close'
        wsLoading.value = readyState !== 1
        break
      case 'getChatList':
        if (pageId === currentPageId) {
          handleWorkerApiCallback('getChatList', data)
        }
        break
      case 'getMessageList':
        if (pageId === currentPageId) {
          handleWorkerApiCallback('getMessageList', data)
        }
        break
      case 'storeSync':
        handleStoreSync(data)
        break
      case 'socketClosed':
        chatListNeedReload.value = true
        socketReady.value = false
        socketFirstConnect.value = false
        loadingRooms.value = false
        break
      case 'connectError':
        wsIsError.value = true
        wsLoading.value = false
        break
      case 'logInfo':
        trackGAEvent(data.event, data.params)
        break
    }
  }
  /**
   * 初始化 worker
   * @param heartCheck 检测 worker 是否假死
   */
  const initWorker = (heartCheck = true) => {
    setTimeout(() => {
      const clientInfo = getClientInfo()
      worker.postMessage(
        {
          type: 'init',
          pageId: currentPageId,
          data: {
            /**
             * 设备信息
             */
            clientInfo: toRaw(clientInfo),
            /**
             * 入口信息
             */
            entryInfo
          }
        },
        {
          heartCheck
        }
      )
    }, 200)
  }
  worker.onmessageerror = (err) => {
    console.log('worker message error', err)
  }
  worker.onerror = (err) => {
    console.log('worker error', err)
  }
  worker.onrestart = () => {
    console.log('worker restarted')
    initWorker(false)
  }
  initWorker()

  /**
   * 页面之间的数据同步
   * @param data
   */
  const handleStoreSync = (data: SyncStoreData) => {
    switch (data.action) {
      // 清除未读数
      case 'clearRoomUnreadCount':
        const targetRoom = rooms.value.find((r) => r.roomId === data.roomId)
        if (targetRoom) {
          targetRoom.unreadCount = 0
        }
        break
    }
  }

  /**
   * 向 worker 发送建立连接的消息
   * @param createRoomOpt
   */
  const sendConnectMessage = (createRoomOpt: any = null) => {
    if (wsStatus.value !== 'open') {
      wsLoading.value = true
    }
    if (!createRoomOpt) {
      createRoomOpt = defaultCreateRoomParams.value
    } else {
      defaultCreateRoomParams.value = createRoomOpt
    }
    const clientInfo = getClientInfo()
    const params = {
      vogToken: clientInfo.userToken,
      imUserType: 'CUSTOMER',
      deviceCode: clientInfo.deviceCode,
      country: clientInfo.country,
      currencyCode: clientInfo.currency
    }
    if (params.vogToken) {
      // 开启连接
      worker.postMessage({ type: 'connect', data: params })
    } else {
      rooms.value = []
      messagesAll.value = {}
      messagesPromiseModule['connect'] && messagesPromiseModule['connect'].reject('没有token，不能建立连接')
    }
  }

  /**
   * 建立连接
   * @param createRoomOpt
   * @returns
   */
  const doConnect = (createRoomOpt: any = null) => {
    return singleflight.do('connect', () => {
      return new Promise((resolve, reject) => {
        // 如果已有长连接，不再连接
        if (wsStatus.value === 'open') {
          if (chatListNeedReload.value) {
            getChatList({
              forceRefresh: true
            }).finally(() => {
              resolve(true)
            })
          } else {
            setTimeout(() => {
              resolve(true)
            })
          }
        } else {
          messagesPromiseModule['connect'] = { resolve, reject }
          sendConnectMessage(createRoomOpt)
        }
      }).finally(() => {
        messagesPromiseModule['connect'] = null
        delete messagesPromiseModule['connect']
      })
    })
  }

  /**
   * 强制重新发起连接
   */
  const doReconnect = () => {
    if (messagesPromiseModule['connect']) {
      sendConnectMessage()
    } else {
      doConnect()
    }
  }

  // 处理不同code消息对象
  const handleData = {
    createChat: (data) => {
      const { conversationId, sessionId, isRobot, applyId } = data
      const { headImage, imUserType, nickname, imUserIdStr, imUserId } = imUserData.value
      // 确定已选中的
      const newRoom = {
        roomId: conversationId,
        roomName: '',
        sessionId,
        sessionOpen: true,
        robot: isRobot,
        toUserRole: defaultToUserRole,
        users: [{ _id: imUserIdStr, username: nickname }, reviewUser]
      }
      let newRooms = [...rooms.value]
      const roomIndex = rooms.value.findIndex((room) => room.roomId === conversationId)
      if (roomIndex > -1) {
        newRooms = newRooms.map((room) => {
          if (room.roomId === conversationId) {
            return Object.assign(room, {
              sessionId,
              sessionOpen: true,
              roomName: newRooms[roomIndex].roomName,
              robot: isRobot
            })
          }
          return room
        })
      } else {
        // 临时列表创建的新的房间，直接更新原始房间的id，避免列表多次添加
        if (applyId === createRoomCatch.value.applyId) {
          const originCatchId = Number(createRoomCatch.value.catchConversationId);
          const roomCIndex = rooms.value.findIndex((room) => room.roomId === originCatchId)
          if (roomCIndex > -1) {
            newRooms = newRooms.map((room) => {
              if (room.roomId === originCatchId) {
                return Object.assign(room, {
                  conversationId,
                  roomId: conversationId,
                  sessionId,
                  sessionOpen: true,
                  robot: isRobot
                })
              }
              return room
            })
          } else {
            newRooms.unshift(newRoom)
          }
          createRoomCatch.value = {
            applyId: '',
            catchConversationId: 0,
          }
        } else {
          newRooms.unshift(newRoom)
        }
      }

      if (messagesPromiseModule[`createroom_${applyId}`]) {
        rooms.value = newRooms
        messagesPromiseModule[`createroom_${applyId}`].resolve(data)
      } else {
        // 非当前页面的创建房间，需要刷新列表
        chatListNeedReload.value = true
      }
    },
    applyForId: (data) => {
      const { applyId } = data
      // data: messageId, needClientAck, applyId
      if (messagesPromiseModule[applyId]) {
        messagesPromiseModule[applyId].applyForIdResolve(data)
      }
    },
    receiveSendMsg: (data: ContentMessageToSend) => {
      // 新收到消息时，更新room列表
      const { conversationId } = data
      const room = rooms.value.find((r) => r.roomId === conversationId)
      if (room) {
        Object.assign(room, {
          lastMessage: {
            content: getLastMessageContent(data),
            timestamp: formatLocalTime('HH:mm'),
            date: formatLocalTime('DD/MM/YYYY')
          }
        })
      }
    },
    filterReceiveMessage: (data, isSend?: boolean) => {
      // data: content, conversationId, messageId, needAck
      confirmAckService(data, isSend)
    },
    receiveNewMessageReaction: (data) => {
      const { replyMessageId, emoji, del, conversationId, nickname } = data
      const messages = messagesAll.value[conversationId] || []

      messagesAll.value[conversationId] = messages.map((i) => {
        if (i._id === replyMessageId) {
          const reactions = i.reactions || {}
          let arr = reactions[`${decodeURI(emoji)}`] || []

          if (del || arr.find((i) => i === nickname)) {
            arr = arr.filter((i) => i !== nickname)
          } else {
            arr = [...arr, nickname]
          }
          if (arr.length) {
            reactions[`${decodeURI(emoji)}`] = arr
          } else {
            delete reactions[`${decodeURI(emoji)}`]
          }
          return { ...i, reactions }
        }
        return i
      })
    },
    confirmAckClient: (data) => {
      const { messageId } = data
      if (messagesPromiseModule[`20000_${messageId}`]) {
        messagesPromiseModule[`20000_${messageId}`].resolve(data)
      }
      if (messagesPromiseModule[`20010_${messageId}`]) {
        messagesPromiseModule[`20010_${messageId}`].resolve(data)
      }
      if (messagesPromiseModule[`20026_${messageId}`]) {
        messagesPromiseModule[`20026_${messageId}`].resolve(data)
      }
    },
    getWelcomeMsg: (data) => {
      welcomeMsg.value = data
      const { conversationId, sessionId, nickname, headImage } = data
      // 欢迎语
      const newRooms = rooms.value.map((e) => {
        if (e.roomId === conversationId) {
          return {
            ...e,
            roomId: conversationId,
            sessionId,
            roomName: nickname,
            nickname,
            avatar: e.headImage || headImage || generateCircleWithLetter(nickname.slice(0, 1))
          }
        }
        return e
      })
      rooms.value = newRooms
    },
    receiveIMUserInfo: async (data) => {
      const { imUserId } = data
      imUserData.value = Object.assign(data, {
        imUserIdStr: imUserId.toString()
      })
      let roomListRes: any = {}
      if (isSingleRoom()) {
        const res = await doCreateRoom({
          toUserRole,
          categoryExperts: route.query?.categoryExperts ? Number(route.query?.categoryExperts) : null
        })
        if (res) {
          roomListRes = await getChatList()
        }
      } else {
        let fetchChatList = true
        // 如果已经有房间列表缓存，并且当前房间在缓存里，不发起房间列表请求
        if (route.name === 'chatroom') {
          const roomId = parseInt(route.params.id as string)
          const room = rooms.value.find((r) => r.roomId === roomId)
          if (room) {
            fetchChatList = false
          }
        }
        if (fetchChatList) {
          roomListRes = await getChatList()
        }
      }
      socketFirstConnect.value = false
      socketReady.value = true
      wsIsError.value = false
      if (messagesPromiseModule['connect']) {
        messagesPromiseModule['connect'].resolve(roomListRes || {})
      }
    },
    closeRoomSession: (data) => {
      const { conversationId } = data
      const currentIndex = rooms.value.findIndex((room) => room.roomId === conversationId)
      if (rooms.value[currentIndex]) {
        rooms.value[currentIndex].sessionOpen = false
      }
    },
    confirmRead: (data) => {
      const { batchMessageIds, conversationId } = data
      const messages = messagesAll.value[conversationId] || []
      let refresh = false
      messages.forEach((message) => {
        const isSelf = String(imUserData.value.imUserIdStr) === message.senderId
        if (!message.seen && isSelf) {
          if (batchMessageIds.includes(message._id)) {
            message.seen = true
            refresh = true
          }
        }
      })
      if (refresh) {
        messagesAll.value[conversationId] = messages.concat([])
      }
      const room = rooms.value.find((room) => room.roomId === conversationId)
      if (room) {
        room.unreadCount = Math.max(0, room.unreadCount - batchMessageIds.length)
      }
    },
    systemInfo: (data) => {
      const { conversationId, contentTranslate } = data
      const applyId = v4().replace(/-/g, '').toUpperCase().slice(0, 27)
      const newMsg = {
        _id: applyId,
        content: contentTranslate,
        senderId: imUserData.value.imUserIdStr,
        timestamp: formatLocalTime('HH:mm'),
        date: formatLocalTime('DD/MM/YYYY'),
        loading: false,
        system: true
      }
      if (messagesAll.value[conversationId] && messagesAll.value[conversationId].length >= 0) {
        messagesAll.value[conversationId].push(newMsg)
      } else {
        messagesAll.value[conversationId] = [newMsg]
      }
    },

    systemInfoNewType: (data) => {
      asyncUpdateMessagesAll(data.conversationId || selectedConversationId.value, data, (e) => {
        const { conversationId, content, messageId } = e
        const applyId = messageId || v4().replace(/-/g, '').toUpperCase().slice(0, 27)
        removeMsgByCurrent(applyId)

        const newMsg = {
          _id: applyId,
          content: content,
          senderId: '0',
          timestamp: formatLocalTime('HH:mm'),
          date: formatLocalTime('DD/MM/YYYY'),
          loading: false,
          custom: true,
          system: false,
          newSystemType: true
        }
        if (messagesAll.value[conversationId] && messagesAll.value[conversationId].length >= 0) {
          messagesAll.value[conversationId].push(newMsg)
        } else {
          messagesAll.value[conversationId] = [newMsg]
        }
      })
    },

    /**
     * 接受tab
     */
    beforeCreateTabMsg: (data) => {
      selectedConversationId.value && asyncUpdateMessagesAll(data.conversationId, data, (e) => {
        const { conversationId } = e
        const selectedMessages = messagesAll.value[conversationId] || []
        if (tabMsgId.value) {
          const exists = selectedMessages.find((r) => r._id === tabMsgId.value)
          if (exists) {
            return
          }
        }

        const last = selectedMessages[selectedMessages.length - 1]

        let _tabMsgId = Date.now()
        if (last) {
          _tabMsgId = last._id + 1
        }

        const tabMsg = {
          _id: _tabMsgId,
          content: '',
          timestamp: formatLocalTime('HH:mm'),
          date: formatLocalTime('DD/MM/YYYY'),
          senderId: 0,
          loading: false,
          system: false,
          custom: true,
          tabInfo: data,
          local: true
        }

        tabMsgId.value = _tabMsgId

        messagesAll.value = Object.assign({}, messagesAll.value, {
          [conversationId]: [...selectedMessages, tabMsg]
        })
      })
    }
  }


  /**
   * 生成等待消息
   */
  const createWaitingMsg = () => {
    asyncUpdateMessagesAll(selectedConversationId.value, {}, () => {
      const selectedMessages = messagesAll.value[selectedConversationId.value || ''] || []
      const last = selectedMessages[selectedMessages.length - 1]

      let _waitMsgId = Date.now()
      if (last) {
        _waitMsgId = last._id + 1
      }

      const waitMsg = {
        _id: _waitMsgId,
        content: '',
        timestamp: formatLocalTime('HH:mm'),
        date: formatLocalTime('DD/MM/YYYY'),
        senderId: 0,
        custom: true,
        loading: false,
        local: true
      }

      waitMsgId.value = _waitMsgId

      messagesAll.value = Object.assign({}, messagesAll.value, {
        [selectedConversationId.value || '']: [...selectedMessages, waitMsg]
      })
    })
  }

  const handleReceiveMessage = async (receiveData) => {
    if (receiveData) {
      const { code, data } = receiveData
      switch (code) {
        case 10001: // 服务端->客户端，确认客户端发送消息(20001)已收到
          handleData.confirmAckClient(data)
          break
        case 10009: // 接口已升级，页面还是老的，强制刷新页面
          window.location.reload()
        case 20000: // 自己发出的消息 客户端->服务端->其他客户端
          handleData.receiveSendMsg(data)
          break
        case 20001: // 发送普通消息 服务端->客户端
          handleData.filterReceiveMessage(data)
          break
        case 20002: // 消息发送多端同步 服务端->客户端
          handleData.filterReceiveMessage(data, true)
          break
        case 20005: // 欢迎语句
          handleData.getWelcomeMsg(data)
          break
        case 20010: // 确认已读
        case 20011: // 确认已读
        case 20012: // 确认已读
          handleData.confirmRead(data)
          break
        case 20016: // 服务端->客户端: 表情回复
        case 20017: // 服务端->客户端: 表情回复多端同步
          handleData.receiveNewMessageReaction(data)
          break
        case 20022: // 申请下发Id
          handleData.applyForId(data)
          break
        case 20023: // 创建对话
          if (getIsPlatformPC()) {
            jsBridge.callPromise({ noResponse: true }, EVENT_VENDOR_CALLBACK, {
              ...data,
              key: 'session_start'
            })
          }
          handleData.createChat(data)
          break
        case 20024: // 获取用户基本信息
          handleData.receiveIMUserInfo(data)
          break
        case 20025:
          handleData.closeRoomSession(data)
          break
        case 20032: // 系统消息
          handleData.systemInfo(data)
          break
        case 20033: // 系统消息
          handleData.systemInfoNewType(data)
          break
        case 20035: // 发送消息 服务端->客户端
          handleData.filterReceiveMessage(
            {
              ...data,
              afterQueueSend: true
            },
            true
          )
          break
        case 20040: // 机器人tab消息
          handleData.beforeCreateTabMsg(data)
          break
      }
    }
  }

  /**
   * 本地新增一条消息
   * @param data
   */
  const addNewMessage = (data: IMMessage) => {
    const { content, files, roomId, applyId, loading, msgType, card, replyMessage } = data
    let newMsg = {}
    if (msgType === 'card') {
      newMsg = {
        _id: applyId,
        content: '[card]',
        senderId: imUserData.value.imUserIdStr,
        timestamp: formatLocalTime('HH:mm'),
        date: formatLocalTime('DD/MM/YYYY'),
        loading,
        files,
        card: { ...card, isSend: true }
      }
    } else if (msgType === 'file') {
      newMsg = {
        _id: applyId,
        content: '',
        senderId: imUserData.value.imUserIdStr,
        timestamp: formatLocalTime('HH:mm'),
        date: formatLocalTime('DD/MM/YYYY'),
        loading,
        files: files?.map((e) => ({
          name: e.name,
          size: e.size,
          extension: e.extension,
          type: e.type,
          audio: true,
          preview: e.localUrl,
          url: e.url || e.localUrl,
          width: e.width,
          height: e.height,
          compressed: e.compressed
        }))
      }
    } else {
      newMsg = {
        _id: applyId,
        content: content,
        senderId: imUserData.value.imUserIdStr,
        timestamp: formatLocalTime('HH:mm'),
        date: formatLocalTime('DD/MM/YYYY'),
        loading,
        files,
        type: msgType
      }
    }
    const newMessages = (messagesAll.value[roomId] || []).concat({
      ...newMsg,
      replyMessage
    })
    messagesAll.value = Object.assign({}, messagesAll.value, {
      [roomId]: newMessages
    })
  }

  /**
   * 将消息更新为发送失败
   * @param messageInfo
   */
  const setMessageFailed = (messageInfo: ContentMessageToSend) => {
    const message = (messagesAll.value[messageInfo.conversationId] || []).find(
      (r) => r._id === messageInfo.applyId || r._id === messageInfo.messageId
    )
    if (message && message.loading) {
      message.loading = false
      message.failure = true
      messagesAll.value[messageInfo.conversationId] = messagesAll.value[messageInfo.conversationId].concat([])
    }
    if (latestPendingMessage.value && latestPendingMessage.value.applyId === messageInfo.applyId) {
      latestPendingMessage.value = null
    }
  }

  /**
   * 将消息更新为发送成功
   * @param messageInfo
   */
  const setMessageSuccess = (messageInfo: ContentMessageToSend) => {
    const message = (messagesAll.value[messageInfo.conversationId] || []).find(
      (r) => r._id === messageInfo.applyId || r._id === messageInfo.messageId
    )
    if (message && message.loading) {
      message.loading = false
      messagesAll.value[messageInfo.conversationId] = messagesAll.value[messageInfo.conversationId].concat([])
    }
    if (latestPendingMessage.value && latestPendingMessage.value.applyId === messageInfo.applyId) {
      latestPendingMessage.value = null
    }
  }

  /**
   * 更新消息 id
   * @param messageInfo
   */
  const updateMessageId = (messageInfo: ContentMessageToSend) => {
    const message = (messagesAll.value[messageInfo.conversationId] || []).find((r) => r._id === messageInfo.applyId)
    if (message) {
      message._id = messageInfo.messageId
    }
  }

  const beforeSendMessage = async (msgInfo: ContentMessageToSend) => {
    const conversationId = msgInfo.conversationId
    const selectedRoomInfo = getRoomInfoByConversationId(conversationId)
    if (!selectedRoomInfo.sessionOpen) {
      // 房间已断开连接，重新创建房间
      const { toCombineId = defaultToCombinId, toUserRole = defaultToUserRole, categoryExperts } = selectedRoomInfo
      return doCreateRoom({ toUserRole, conversationId, categoryExperts })
    } else {
      return true
    }
  }

  const fetchMessageId = async (options?: { beforeFn?: Function }, ownedApplyId?: string): Promise<any> => {
    const applyId = ownedApplyId || v4().replace(/-/g, '').toUpperCase().slice(0, 27)
    if (options && options.beforeFn) {
      options.beforeFn(applyId)
    }
    const promise = new Promise((resolve, reject) => {
      const applyForIdStr = `20022:${JSON.stringify({ applyId })}`
      worker.postMessage(
        { type: 'msg', data: applyForIdStr },
        {
          heartCheck: SEND_MESSAGE_TIMEOUT
        }
      )
      const timeout = setTimeout(() => {
        reject(new Error('timeout'))
      }, SEND_MESSAGE_TIMEOUT)
      messagesPromiseModule[applyId] = { applyForIdResolve: resolve, timeout }
    }).finally(() => {
      if (messagesPromiseModule[applyId].timeout) {
        clearTimeout(messagesPromiseModule[applyId].timeout)
      }
      messagesPromiseModule[applyId] = null
      delete messagesPromiseModule[applyId]
    })
    return promise
  }

  /**
   * 准备即将要发送的消息
   * @param msgData
   * @returns
   */
  const prepareContentMessageToSend = async (msgInfo: ContentMessageToSend, msgData: IMMessage) => {
    if (isMessagePrepared(msgInfo)) {
      return
    }
    const { roomId, msgType, files = [] } = msgData || {}
    const applyId = msgInfo.applyId
    if (msgType === 'file') {
      const message = messagesAll.value[roomId].find((r) => r._id === applyId)
      return singleflight.do(`upload_message_file_${applyId}`, async () => {
        const firstFile = files[0]
        if (msgData.compressed) {
          firstFile.compressed = true
        }
        try {
          const res = await handleUploadFile(firstFile)
          const fileUrl = res.data
          if (msgInfo.multimedia) {
            msgInfo.multimedia.url = fileUrl
            msgInfo.multimedia.fileType = files?.[0].type
            msgInfo.multimedia.extension = files?.[0].extension
          }
          if (message && firstFile.blob) {
            // 同步到消息
            const messageFile = message.files[0]
            messageFile.url = fileUrl
            message.compressed = firstFile.compressed
            if (firstFile.compressed) {
              if (messageFile.preview) {
                cachedBlob.delete(messageFile.preview)
                URL.revokeObjectURL(messageFile.preview)
              }
              messageFile.preview = URL.createObjectURL(firstFile.blob)
              cachedBlob.set(messageFile.preview, firstFile.blob)
            }
          }
        } catch (err) {
          if (firstFile.compressed && message && firstFile.blob) {
            message.compressed = true
            const messageFile = message.files[0]
            if (messageFile.preview) {
              cachedBlob.delete(messageFile.preview)
              URL.revokeObjectURL(messageFile.preview)
            }
            messageFile.preview = messageFile.url = URL.createObjectURL(firstFile.blob)
            cachedBlob.set(messageFile.preview, firstFile.blob)
          }
          throw err
        }
      })
    }
  }

  const sendMsgInfo = async (msgInfo: ContentMessageToSend) => {
    const messageId = msgInfo.messageId
    const promise = new Promise((resolve, reject) => {
      messagesPromiseModule[`20000_${messageId}`] = { resolve, reject }
      const messageUploadIdStr = `20000:${JSON.stringify(msgInfo)}`
      // 向worker发送
      worker.postMessage({ type: 'msg', data: messageUploadIdStr })

      // 如果超过5s没返回，则自动报错
      setTimeout(() => {
        if (messagesPromiseModule[`20000_${messageId}`]) {
          messagesPromiseModule[`20000_${messageId}`].reject({
            messageId,
            errorMsg: 'timeout'
          })
        }
      }, SEND_MESSAGE_TIMEOUT)
    }).finally(() => {
      messagesPromiseModule[`20000_${messageId}`] = null
      delete messagesPromiseModule[`20000_${messageId}`]
    })
    return promise
  }

  /**
   * 最后一条正在发送的消息，主要用于排队时通知后端
   */
  const latestPendingMessage = ref<ContentMessageToSend | null>(null)

  /**
   * 部分手机（三星）会丢失 blob 和 object url 的引用关系，建立一个强引用，在文件消息发送成功后再移除
   */
  const cachedBlob = new Map()

  const doSendMessage = async (msgData: IMMessage) => {
    await doConnect()
    // 新消息发送后，更新被选中的roomId
    const { roomId, replyMessage, files } = msgData || {}

    if (files && files.length > 0) {
      const firstFile = files[0]
      if (!firstFile.blob) {
        // 尝试从全局缓存的 blob 中还原
        firstFile.blob = cachedBlob.get(firstFile.localUrl)
      } else {
        // 缓存 blob
        cachedBlob.set(firstFile.localUrl, firstFile.blob)
      }
    }

    // 设置已拥有的applyId
    const ownedApplyId = v4().replace(/-/g, '').toUpperCase().slice(0, 27)
    // 提前在界面上展示文案并loading
    addNewMessage({ ...msgData, applyId: ownedApplyId, loading: true })
    // 格式化消息，将 im 消息转为待发送的消息
    const selectedRoomInfo = getRoomInfoByConversationId(roomId)
    const msgSendInfo = filterMsgSendInfo(msgData, roomId, selectedRoomInfo) as ContentMessageToSend
    msgData.messageType = msgSendInfo.messageType
    msgSendInfo.applyId = ownedApplyId
    latestPendingMessage.value = msgSendInfo

    if (replyMessage) {
      msgSendInfo.replyMessageId = replyMessage._id
    }

    try {
      // 预处理消息，上传文件等
      await prepareContentMessageToSend(msgSendInfo, msgData)
    } catch (err) {
      trackGAEvent(EVENT_SEND_MESSAGE_FAILURE, {
        description: 'upload file error',
        message_type: msgSendInfo.messageType,
        message_id: msgSendInfo.applyId
      })
      return setMessageFailed(msgSendInfo)
    }
    try {
      // 获取messageId
      const msgRes = await fetchMessageId(undefined, ownedApplyId)
      const { messageId }: any = msgRes
      msgSendInfo.messageId = messageId
      updateMessageId(msgSendInfo)
    } catch (err) {
      trackGAEvent(EVENT_SEND_MESSAGE_FAILURE, {
        description: 'fetch message id timeout',
        message_type: msgSendInfo.messageType,
        message_id: msgSendInfo.applyId
      })
      return setMessageFailed(msgSendInfo)
    }
    const isActiveChat: any = await beforeSendMessage(msgSendInfo)
    if (isActiveChat) {
      if (isActiveChat && typeof isActiveChat === 'object' && isActiveChat.sessionId) {
        msgSendInfo.sessionId = isActiveChat.sessionId
      }
      try {
        // 执行发送
        const ackRes = await sendMsgInfo(msgSendInfo)
        const { ackStatus }: any = ackRes
        if (ackStatus === 'SUCCESS') {
          setMessageSuccess(msgSendInfo)
          if (files && files.length > 0) {
            const firstFile = files[0]
            if (firstFile.blob) {
              cachedBlob.delete(firstFile.preview)
            }
          }
        } else {
          setMessageFailed(msgSendInfo)
          trackGAEvent(EVENT_SEND_MESSAGE_FAILURE, {
            description: 'server error',
            message_type: msgSendInfo.messageType,
            message_id: msgSendInfo.applyId
          })
        }
        // 会话列表排序
        doSortRoomList(roomId, msgData, true)
      } catch (errRes) {
        setMessageFailed(msgSendInfo)
        trackGAEvent(EVENT_SEND_MESSAGE_FAILURE, {
          description: 'send message timeout',
          message_type: msgSendInfo.messageType,
          message_id: msgSendInfo.applyId
        })
      }
    } else {
      setMessageFailed(msgSendInfo)
    }
  }

  /**
   * 表情回复
   * @param param0
   */
  const doSendMessageReaction = async ({ messageId, reaction, remove, roomId }) => {
    const selectedRoomInfo = getRoomInfoByConversationId(selectedConversationId.value)

    worker.postMessage({
      type: 'msg',
      data: `20015:${JSON.stringify({
        conversationId: selectedConversationId.value,
        sessionId: selectedRoomInfo.sessionId,
        replyMessageId: messageId,
        emoji: encodeURI(reaction.unicode)
      })}`
    })

    const promise = new Promise((resolve, reject) => {
      messagesPromiseModule[`20000_${messageId}`] = { resolve, reject }
    })
      .finally(() => {
        const { nickname } = imUserData.value

        messagesAll.value[selectedConversationId.value || ''] = messagesAll.value[selectedConversationId.value || '']?.map((i) => {
          if (i._id === messageId) {
            const reactions = i.reactions || {}
            let arr = reactions[`${reaction.unicode}`] || []

            if (remove || arr.find((i) => i === nickname)) {
              arr = arr.filter((i) => i !== nickname)
            } else {
              arr = [...arr, nickname]
            }
            if (arr.length) {
              reactions[`${reaction.unicode}`] = arr
            } else {
              delete reactions[`${reaction.unicode}`]
            }
            return { ...i, reactions }
          }
          return i
        })

        messagesPromiseModule[`20000_${messageId}`] = null
        delete messagesPromiseModule[`20000_${messageId}`]
      })
      .catch((failRes) => {
        console.log('发送失败', failRes)
      })

    return promise
  }

  // 整理会话列表排序
  const doSortRoomList = (roomId, newMsg, isSend?: boolean) => {
    const isnew = !rooms.value.some((room) => room.roomId === roomId)
    const originRooms = [...rooms.value]
    const { imUserIdStr, nickname: imUserNickname } = imUserData.value || {}
    if (isnew) {
      const nickname = newMsg.username || newMsg.nickname
      const newRoom = {
        roomId: roomId,
        roomName: nickname,
        avatar: newMsg.headImage || generateCircleWithLetter(nickname.slice(0, 1)),
        latestContent: newMsg.content,
        latestTime: newMsg.timestamp,
        unreadCount: 0,
        lastMessage: { ...newMsg, content: getLastMessageContent(newMsg) },
        users: [{ _id: imUserIdStr, username: imUserNickname }, { _id: roomId, username: nickname }, reviewUser]
      }
      // originRooms.unshift(newRoom)
      //
      // 不再通过unshift，而是通过chatListNeedReload来触发列表刷新，避免在列表中插入新数据时
      chatListNeedReload.value = true
    }
    const selectRoomIndex = originRooms.findIndex((room) => room.roomId === roomId)
    if (!isSend && originRooms[selectRoomIndex] && !newMsg.system && !newMsg.seen) {
      originRooms[selectRoomIndex].unreadCount = originRooms[selectRoomIndex].unreadCount + 1
    }
    let newLastMsg = {}
    if (/image\/.+/.test(newMsg.fileType)) {
      newLastMsg = { ...newMsg, content: '【Picture】' }
    } else if (/video\/.+/.test(newMsg.fileType)) {
      newLastMsg = { ...newMsg, content: '【Video】' }
    } else if (newMsg.fileType) {
      newLastMsg = { ...newMsg, content: '【File】' }
    } else {
      newLastMsg = newMsg
    }
    originRooms[selectRoomIndex].lastMessage = newLastMsg
    // const updateRooms = originRooms.splice(selectRoomIndex, 1);
    rooms.value = originRooms //[...updateRooms, ...originRooms];
  }

  // 确认收到信息
  const confirmAckService = (data, isSend?: boolean) => {
    const { messageId, conversationId, needAck } = data
    // 添加页面的messages数据
    let newMsg: any = null
    if (needAck) {
      // 确认已收到消息
      const msgTxt = `10002:${JSON.stringify({
        ackStatus: 'SUCCESS',
        messageId
      })}`
      worker.postMessage({ type: 'msg', data: msgTxt })
    }
    data.read = isSend
    newMsg = filterMsgTypeInfo(data, isSend, staticInfo, imUserData, t)
    const newMessages = (messagesAll.value[conversationId] || []).concat([newMsg])

    if (messagesPromiseModule[`sendQueue_${conversationId}`]) {
      messagesPromiseModule[`sendQueue_${conversationId}`].resolve()
    }

    messagesAll.value = Object.assign({}, messagesAll.value, {
      [conversationId]: newMessages
    })

    if (['CARD_GOODS_DETAIL', 'CARD_ORDER_DETAIL'].includes(data.messageType)) {
      if (messagesPromiseModule[`welcome_${conversationId}`]) {
        messagesPromiseModule[`welcome_${conversationId}`].resolve()
      }
    }
    if (['CARD_GOODS_DETAIL', 'CARD_ORDER_DETAIL'].includes(data.messageType)) {
      if (messagesPromiseModule[`20007_${conversationId}`]) {
        messagesPromiseModule[`20007_${conversationId}`].resolve(newMsg)
      }
    }

    // 会话列表排序
    doSortRoomList(conversationId, newMsg, data.read)
  }

  /**
   * 清除未读数
   * @param roomInfo
   */
  const clearRoomUnreadCount = (roomInfo: Pick<Room, 'roomId'>) => {
    setTimeout(() => {
      worker.postMessage({
        type: 'storeSync',
        data: {
          action: 'clearRoomUnreadCount',
          roomId: roomInfo.roomId
        }
      })
    }, 50)
  }

  /**
   * 获取房间消息列表
   * @param roomInfo
   * @param updateMode 列表更新方式
   * @param isFirstPageMsgGet 是否首次获取
   * @returns
   */
  const getMessageList = async (
    roomInfo: Room,
    updateMode: MessageListUpdateMode,
    isFirstPageMsgGet: boolean = false
  ) => {
    let selectedMessages: IMMessage[] = messagesAll.value[roomInfo.roomId] || []
    if (updateMode === MessageListUpdateMode.Reset) {
      selectedMessages = selectedMessages.filter((r) => r.local)
      messagesAll.value[roomInfo.roomId] = []
    }
    const lastMessage = selectedMessages[0] || null

    const clientInfo = getClientInfo()
    const params = {
      userType: 'CUSTOMER',
      voghionToken: clientInfo.userToken,
      conversationId: roomInfo.roomId,
      startMessageId: lastMessage ? lastMessage._id : '',
      limit: pageSize
    }
    if (updateMode === MessageListUpdateMode.Reset || updateMode === MessageListUpdateMode.Refresh) {
      params.startMessageId = ''
    }
    let res
    try {
      messagesLoaded.value = false
      messagesLoading.value = true

      res = await fetchMessageList(params)
      if (res.rtn_code === '0000') {
        clearRoomUnreadCount(roomInfo)
        const messages = res.data.messages || []
        let resMessages = selectedMessages
        if (!messages.length) {
          messagesLoaded.value = true
        } else {
          const list = messages.map((e) => filterMsgTypeInfo(e, true, staticInfo, imUserData, t))

          if (list.length) {
            let newSelectedMessageList
            if (updateMode === MessageListUpdateMode.Refresh) {
              // 如果是刷新，检测当前第一页消息的最后一条是否在已有消息列表中，如果不再，说明中间还有消息没获取，此时清空已有的消息列表
              const lastMessage = list[list.length - 1]
              const exists = selectedMessages.find((r) => r._id === lastMessage._id)
              if (!exists) {
                // 保留本地消息和待发送的消息
                selectedMessages = selectedMessages.filter((r) => r.local || r.loading)
              }
              newSelectedMessageList = list.concat(selectedMessages.reverse()).reverse()
            } else {
              newSelectedMessageList = selectedMessages.reverse().concat(list).reverse()
            }
            // 去重
            resMessages = newSelectedMessageList.reduce((acc, current) => {
              const existingItem = acc.find((item) => item && item._id === current._id)
              if (!existingItem) {
                acc.push(current)
              }
              return acc
            }, [])
            resMessages.sort((a, b) => Number(a._id) - Number(b._id))
          }
          messagesLoading.value = false
        }
        // 首次打开显示，后续删除
        // if (isFirstPageMsgGet) {
        //   const last: any = resMessages[resMessages.length - 1]

        //   let _warnMsgId = Date.now()
        //   if (last) {
        //     _warnMsgId = last._id + 11
        //   }

        //   let resMessage = {
        //     _id: _warnMsgId,
        //     content: '',
        //     senderId: '0',
        //     roomId: roomInfo.roomId,
        //     timestamp: formatLocalTime('HH:mm'),
        //     date: formatLocalTime('DD/MM/YYYY'),
        //     loading: false,
        //     system: false,
        //     custom: true,
        //     local: true
        //   }
        //   if (last && last.tabInfo) {
        //     resMessages.splice(resMessages.length - 2, 0, resMessage);
        //   } else {
        //     resMessages.push(resMessage)
        //   }

        //   warnMsgId.value = _warnMsgId
        // }

        messagesAll.value = Object.assign({}, messagesAll.value, {
          [roomInfo.roomId]: [...resMessages]
        })
      }
    } catch (err) {
      messagesLoading.value = false
    }

    if (messagesPromiseModule[`asyncUpdateMessages_${roomInfo.roomId}`]) {
      executeInOrder(messagesPromiseModule[`asyncUpdateMessages_${roomInfo.roomId}`])
    }
    return res
  }

  const clearWarnMsgById = () => {
    if (warnMsgId.value) {
      const selectedMessages = messagesAll.value[selectedConversationId.value || ''] || []
      let resMessages = selectedMessages.filter((msg) => msg._id !== warnMsgId.value)
      messagesAll.value = Object.assign({}, messagesAll.value, {
        [selectedConversationId.value || '']: [...resMessages]
      })

      warnMsgId.value = 0
    }
  }

  const chatPageIndex = ref(1)

  /**
   * 获取会话列表
   * 1. 暂时不考虑分页，用户只能和平台客服对话，只有一个
   * 2. 正常情况下，只有断连后才需要强制刷新一次
   * @param opt
   * @returns
   */
  const getChatList = async (params: GetChatListRequest = {}) => {
    const forceRefresh = !!params.forceRefresh
    if (!forceRefresh) {
      if (rooms.value.length >= roomsTotal.value && roomsTotal.value > 0) {
        chatsLoaded.value = true
        return
      }
    }
    const { toCombineId } = defaultCreateRoomParams.value
    const clientInfo = getClientInfo()
    const fetchParams = {
      combineId: toCombineId,
      pageSize: chatPageSize,
      pageIndex: chatPageIndex.value,
      imUserId: imUserData.value.imUserId,
      voghionToken: clientInfo.userToken,
      userType: 'CUSTOMER'
    }
    loadingRooms.value = true
    if (!forceRefresh && rooms.value.length === 0) sdkLoadingRooms.value = true
    try {
      const res = await singleflight.do(`fetchChatList_${JSON.stringify(fetchParams)}`, () => {
        return fetchChatList(fetchParams)
      })
      const conversations = res?.data?.conversations || []
      const newRooms = filterRoom(conversations, imUserData)

      roomsTotal.value = res?.data?.totalCount || 0
      if (isSingleRoom()) {
        let o = newRooms.find((m) => m.categoryExperts.toString() === route.query?.categoryExperts)

        if (o) {
          selectedConversationId.value = o.roomId
        }
      } else if (route.params?.id) {
        selectedConversationId.value = Number(route.params.id)
      }
      rooms.value = newRooms

      cacheRooms(toRaw(rooms.value))

      chatListNeedReload.value = false
      loadingRooms.value = false
      if (sdkLoadingRooms.value) {
        sdkLoadingRooms.value = false
        chatsLoaded.value = true
      }
      return res
    } catch (err) {
      loadingRooms.value = false
      if (sdkLoadingRooms.value) {
        sdkLoadingRooms.value = false
        chatsLoaded.value = true
      }
    }
  }

  const getEndpoint = () => {
    return fetchEndpointConfig({})
  }

  // 发送20007消息,获取用户卡片基本信息
  const fetchCardInfo = async (roomId, sessionId, extParam): Promise<any> => {
    const cardStrMsg = `20007:${JSON.stringify({
      conversationId: roomId,
      sessionId,
      extParam: extParam || route.query?.extParam || '',
      toImUserType: defaultToUserType
    })}`
    worker.postMessage({ type: 'msg', data: cardStrMsg })
    const promise = new Promise((resolve) => {
      messagesPromiseModule[`20007_${roomId}`] = { resolve }
    }).finally(() => {
      messagesPromiseModule[`20007_${roomId}`] = null
      delete messagesPromiseModule[`20007_${roomId}`]
    })
    return promise
  }

  // 修改ws语言
  const modifyWsLang = async (shortLang): Promise<any> => {
    const readyMessageRes = await fetchMessageId()
    const { messageId }: any = readyMessageRes

    const langMsg = `20026:${JSON.stringify({
      autoTranslateStatus: 'OPEN',
      autoTranslateLanguage: shortLang,
      messageId
    })}`
    worker.postMessage({ type: 'msg', data: langMsg })
    const promise = new Promise((resolve) => {
      messagesPromiseModule[`20026_${messageId}`] = { resolve }
    }).finally(() => {
      messagesPromiseModule[`20026_${messageId}`] = null
      delete messagesPromiseModule[`20026_${messageId}`]
    })
    return promise
  }

  // 如果isSingleRoom()为true，表示来自订单页或者商品页，发送前端层卡片
  const doLocalSendCard = debounce(1000, ({ roomId, sessionId }) => {
    const { extParam } = defaultCreateRoomParams.value
    const selectedMessages = messagesAll.value[roomId] || []
    const hasCard = selectedMessages.some((e) => e && e.card && !e.card.isSend)
    // 历史列表里没有卡片
    if (!hasCard) {
      // 且必须是订单列表、订单详情、商品详情
      if (isSingleRoom()) {
        fetchCardInfo(roomId, sessionId, extParam)
        doSendWelcome(roomId)
      }
    }
  })

  const doSendWelcome = (roomId) => {
    const promise = new Promise((resolve) => {
      messagesPromiseModule[`welcome_${roomId}`] = { resolve }
    }).then(() => {
      messagesPromiseModule[`welcome_${roomId}`] = null
      delete messagesPromiseModule[`welcome_${roomId}`]
      const resMessages = messagesAll.value[roomId] || []
      const isWelcom = resMessages.some((m) => m.isWelcom)


      if (!isWelcom && welcomeMsg.value.contentTranslate) {
        const applyId = v4().replace(/-/g, '').toUpperCase().slice(0, 27)
        resMessages.push({
          _id: applyId,
          content: welcomeMsg.value.contentTranslate,
          senderId: '0',
          username: '',
          timestamp: formatLocalTime('HH:mm'),
          date: formatLocalTime('DD/MM/YYYY'),
          type: 'text',
          isWelcom: true
        })
      }
    })
    return promise
  }

  const activeTriggerPromise = (promiseKey) => {
    if (messagesPromiseModule[promiseKey]) {
      messagesPromiseModule[promiseKey].resolve()
    }
  }

  // 再次发送错误消息
  const doSendFailureAgain = async (message) => {
    const conversationId = selectedConversationId.value || ''
    const selectedMessages = messagesAll.value[conversationId] || []
    const newMessages: any[] = []
    let failMessage = null
    selectedMessages.forEach((e) => {
      if (e._id === message._id) {
        failMessage = message
      } else {
        if (e) {
          newMessages.push(e)
        }
      }
    })

    messagesAll.value = { ...messagesAll.value, [conversationId]: newMessages }
    if (failMessage) {
      const currentIndex = rooms.value.findIndex((room) => room.roomId === conversationId)
      rooms.value[currentIndex].sessionOpen = false
    }
    let msgType = 'text'
    if (message.card) {
      msgType = 'card'
    } else if (message.files && message.files.length > 0) {
      msgType = 'file'
    }
    await doSendMessage({
      ...message,
      roomId: conversationId,
      failure: false,
      msgType
    })
  }

  const doViewMsg = async (batchMessageIds, conversationId, messageId) => {
    const viewStr = `20010:${JSON.stringify({
      batchMessageIds: batchMessageIds.filter((id) => typeof id === 'number'),
      conversationId,
      messageId
    })}`
    const promise = new Promise((resolve) => {
      worker.postMessage({ type: 'msg', data: viewStr })
      messagesPromiseModule[`20010_${messageId}`] = { resolve }
    }).finally(() => {
      messagesPromiseModule[`20010_${messageId}`] = null
      delete messagesPromiseModule[`20010_${messageId}`]
    })
    return promise
  }

  const viewMessageQueue = {}
  const triggerMessageView = throttle(3000, async (conversationId) => {
    try {
      const readyMessageRes = await fetchMessageId()
      const { messageId }: any = readyMessageRes
      const batchMessageIds = Array.from(viewMessageQueue[conversationId] || [])
      if (batchMessageIds.length) {
        const viewRes: any = await doViewMsg(batchMessageIds, conversationId, messageId)
        const { ackStatus } = viewRes
        if (ackStatus === 'SUCCESS') {
          const room = rooms.value.find((r) => String(r.roomId) === String(conversationId))
          if (room) {
            // 会话未读数目前都做全清
            clearRoomUnreadCount(room)
          }
          if (viewMessageQueue[conversationId]) {
            const queue = viewMessageQueue[conversationId].filter((r) => !batchMessageIds.includes(r))
            if (!queue.length) {
              delete viewMessageQueue[conversationId]
            } else {
              viewMessageQueue[conversationId] = queue
            }
          }
        }
      }
    } catch (err) { }
  })

  /**
   * 消息曝光
   * @param batchMessageIds
   * @param conversationId
   * @returns
   */
  const viewMessages = async (batchMessageIds: (string | number)[], conversationId: number) => {
    if (!batchMessageIds || batchMessageIds.length === 0) return
    if (!viewMessageQueue[conversationId]) {
      viewMessageQueue[conversationId] = []
    }
    const queue = viewMessageQueue[conversationId]
    const messages = messagesAll.value[conversationId] || []
    /**
     * 先放入队列
     */
    for (const messageId of batchMessageIds) {
      messages.forEach((message) => {
        if (message._id === messageId) {
          message.seen = true
          if (!message.local && !queue.includes(messageId)) {
            queue.push(messageId)
          }
        }
      })
    }
    messagesAll.value[conversationId] = messages.concat([])
    if (viewMessageQueue[conversationId].length) {
      triggerMessageView(conversationId)
    }
  }

  const doViewAllChat = async () => {
    const { headImage, imUserType, nickname, imUserIdStr, imUserId } = imUserData.value
    const params = {
      imUserId,
      imUserType: imUserType || 'CUSTOMER'
    }
    // 先清除数字 再调用接口
    rooms.value.forEach((room) => {
      room.unreadCount = 0
    })
    return clearAllChat().then(
      () => { },
      () => { }
    )
  }

  const getEvaluationConfig = async (params) => {
    const res = await fetchEvaluationConfig(params)
    console.log('res', res)
    return res
  }

  const sendMenuCard = async (data, type) => {
    const selectedIndex = rooms.value.findIndex((room) => room.roomId === selectedConversationId.value)
    const { roomId, sessionId } = rooms.value[selectedIndex]
    let extParam: string | null = null
    let from = ''
    if (type === 'order') {
      from = 'chatBoxOrder'
      extParam = stringToBase64(
        JSON.stringify({
          customerResource: 'CHAT_BOX_ORDER',
          goodsId: data.goods.goodsId,
          showDetailId: data.goods.showDetailId
        })
      )
    } else if (['favorites', 'viewed', 'cart'].includes(type)) {
      from = 'chatBoxGoods'
      extParam = stringToBase64(
        JSON.stringify({
          customerResource: 'CHAT_BOX_GOODS',
          goodsId: data.goods.goodsId,
          showDetailId: ''
        })
      )
    }
    // 发送卡片则发送20007，则直接未读-1，因为马上回返回个20001
    rooms.value[selectedIndex].unreadCount = rooms.value[selectedIndex].unreadCount - 1
    const cardMsg = await fetchCardInfo(roomId, sessionId, extParam)
    doSendMessage({ ...cardMsg, msgType: IMMessageType.Card } as IMMessage)
    doSendQueueMsg({ from })
  }

  const getDiversionIm = async (data) => {
    const res = await fetchDiversionIM(data)
    return res
  }

  const getSelecteRoom = () => {
    const seletRoom = getRoomInfoByConversationId(selectedConversationId.value)
    return seletRoom
  }

  // 弃用
  // const getDefaultChat = async (): Promise<any> => {
  //   /**
  //    * 网络请求失败使用默认数据
  //    */
  //   let res = {
  //     data: {
  //       icon: "https://g-images.voghion.com/productImages/376_1720777517279_voghion90x90.png",
  //       url: `${window.location.origin}${window.location.pathname
  //         }#/?pageFrom=imConversationList&toCombineId=${isProd() ? "6" : "1"
  //         }&toUserRole=GROUP_KEY_ACCOUNT_MANAGER_SALES_BEFORE&toUserType=GROUP&hasOrder=0`,
  //     },
  //   };
  //   try {
  //     res = await fetchDefaultChat({ resourceName: "imConversationList" });
  //   } catch (err) { }
  //   const data = res.data;
  //   const iconUrl = data.icon;
  //   const queryData: { openUrl?: string } = qs.parse(data.url);
  //   const openUrl = queryData.openUrl;
  //   const s = openUrl && openUrl.split("?")[1] ? openUrl.split("?")[1] : "";
  //   const imUrlQuery = s ? qs.parse(s) : {};
  //   const defautChatInfo = { iconUrl, ...imUrlQuery };
  //   worker.postMessage({
  //     type: "updateVipUserInfo",
  //     data: {
  //       headImage: iconUrl,
  //       nickname: "VIP Service",
  //     },
  //   });
  //   return defautChatInfo;
  // };

  /**
   * 关闭房间返回列表
   */
  const closeRoom = () => {
    roomsListOpened.value = false
    setTimeout(() => {
      roomsListOpened.value = true
    })
  }

  const sendWorkerMsg = async (str) => {
    worker.postMessage({ type: 'test', data: str })
  }

  const togglePageVisible = (data) => {
    worker.postMessage({ type: 'visible', data })
  }

  const doSendQueueMsg = ({ tab, from }: { tab?: any; from?: string }) => {
    const selectedRoomInfo = getSelecteRoom()
    if (selectedRoomInfo.robot) {
      selectedRoomInfo.robot = false

      const { pageFrom } = getResource()
      const resourceName = from || pageFrom || 'imConversationList'
      const json = {
        resourceName,
        sessionId: selectedRoomInfo.sessionId,
        consultationTabId: undefined,
        customMessageType: undefined as any
      }

      if (tab) {
        json.consultationTabId = tab.id
      }

      if (latestPendingMessage.value) {
        json.customMessageType = latestPendingMessage.value.messageType
      }

      const viewStr = `20045:${JSON.stringify(json)}`
      worker.postMessage({ type: 'msg', data: viewStr })

      const roomId = selectedRoomInfo.roomId

      // 移除选项卡
      if (tabMsgId.value) {
        tabMsgId.value = 0
      }

      // 开始排队
      createWaitingMsg()

      const promise = new Promise((resolve) => {
        messagesPromiseModule[`sendQueue_${roomId}`] = { resolve }
      })
        .then(() => {
          // 移除排队
          waitMsgId.value = 0
        })
        .finally(() => {
          messagesPromiseModule[`sendQueue_${roomId}`] = null
          delete messagesPromiseModule[`sendQueue_${roomId}`]
        })
    }
  }

  const removeMsgByCurrent = (msgId) => {
    // const selectedRoomInfo = getSelecteRoom()
    const roomId = selectedConversationId.value
    asyncUpdateMessagesAll(roomId, { roomId, msgId }, ({ roomId, msgId }) => {
      const selectedMessages = messagesAll.value[roomId] || []
      let filterMsg = selectedMessages
      filterMsg = selectedMessages.filter((msg) => msg._id !== msgId)

      messagesAll.value = Object.assign({}, messagesAll.value, {
        [roomId]: filterMsg
      })
    })
  }

  if (envStore.inAppTab) {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        setTimeout(() => {
          if (chatListNeedReload.value) {
            doConnect()
          }
        }, 200)
      }
    })
  }

  const showNetworkException = computed(() => {
    return !socketFirstConnect.value
  })



  const asyncUpdateMessagesAll = (conversationId, data, fun) => {
    if (conversationId) {
      console.log('asyncUpdateMessagesAll', conversationId, data);
      if (
        !messagesLoading.value &&
        messagesAll.value &&
        messagesAll.value[conversationId] &&
        messagesAll.value[conversationId].length > 0
      ) {
        fun(data)
      } else {
        new Promise((resolve) => {
          if (messagesPromiseModule[`asyncUpdateMessages_${conversationId}`]) {
            messagesPromiseModule[`asyncUpdateMessages_${conversationId}`].push(resolve)
          } else {
            messagesPromiseModule[`asyncUpdateMessages_${conversationId}`] = [resolve]
          }

        })
          .then(() => {
            fun(data)

          })
          .finally(() => {
            if (messagesPromiseModule[`asyncUpdateMessages_${conversationId}`]) {
              messagesPromiseModule[`asyncUpdateMessages_${conversationId}`] = messagesPromiseModule[`asyncUpdateMessages_${conversationId}`].filter((_, n) => n !== 0)
              if (messagesPromiseModule[`asyncUpdateMessages_${conversationId}`].length === 0) {
                delete messagesPromiseModule[`asyncUpdateMessages_${conversationId}`]
              }
            }
          })
      }
    }
  }

  const executeInOrder = async (functions) => {
    await functions.reduce(async (promiseChain, func) => {
      await promiseChain; // 等待上一个promise完成
      return func(); // 返回新的promise
    }, Promise.resolve()); // 初始promise为已解决状态
  }


  return {
    showNetworkException,
    wsStatus,
    socketReady,
    imUserData,
    rooms,
    loadingRooms,
    sdkLoadingRooms,
    chatsLoaded,
    messagesLoaded,
    messages,
    selectedConversationId,
    wsIsError,
    wsLoading,
    catchMessage,
    doConnect,
    doReconnect,
    doCreateRoom,
    doSendMessage,
    doSendMessageReaction,
    getMessageList,
    clearWarnMsgById,
    chatPageIndex,
    getChatList,
    getEndpoint,
    doLocalSendCard,
    staticInfo,
    doSendFailureAgain,
    doSendWelcome,
    roomChatOpen,
    roomsListOpened,
    closeRoom,
    activeTriggerPromise,
    viewMessages,
    doViewAllChat,
    getEvaluationConfig,
    sendMenuCard,
    getDiversionIm,
    doDisconnect,
    getSelecteRoom,
    warnMsgId,
    sendWorkerMsg,
    togglePageVisible,
    getRoomInfoByConversationId,
    clearRoomUnreadCount,
    waitMsgId,
    tabMsgId,
    doSendQueueMsg,
    messagesAll,
    modifyWsLang,
    initWorker,
    clearCatchMessage
  }
})
